import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { UpdateSkillMetadataRequest } from "../SkillRecord";

import { skillQueryKeys } from "./skillQueryKeys";

interface Args {
  skillId: string;
  request: UpdateSkillMetadataRequest;
}

/**
 * Update a skill's mutable top-level metadata (name, accessibility, tags).
 *
 * Body / description live on :class:`SkillVersionRecord` and are edited via
 * the version flow. Tag changes flow as ``put_tags`` / ``remove_tags`` so
 * concurrent edits to disjoint tags merge cleanly server-side.
 */
export function useUpdateSkillMetadata(orgId: string | undefined) {
  const { skillService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillId, request }: Args) =>
      skillService.updateSkillMetadata(skillId, request, { orgId }),
    onSuccess: (_data, { request }) => {
      void queryClient.invalidateQueries({
        queryKey: skillQueryKeys.org(orgId),
      });
      // A tag change shifts the autocomplete vocabulary. Invalidate only when
      // the changeset actually touched tags so unrelated metadata edits stay
      // cheap.
      const tagsChanged =
        (request.put_tags?.length ?? 0) > 0 ||
        (request.remove_tags?.length ?? 0) > 0;
      if (tagsChanged) {
        void queryClient.invalidateQueries({
          queryKey: skillQueryKeys.orgTags(orgId),
        });
      }
    },
  });
}
